import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  LinearProgress,
  Chip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import {
  Flight,
  Add,
  TrendingUp,
  Star,
  Close,
  Schedule,
  AttachMoney,
  LocationOn,
  Search,
  SwapHoriz,
  Map,
  Analytics
} from '@mui/icons-material';


import FirestoreService from '../services/firestoreService';
import { useAuth } from '../contexts/AuthContext';
import { LoyaltyProgram } from '../types';
import AddProgramDialog from '../components/common/AddProgramDialog';
import EmbeddedFlightMap from '../components/common/EmbeddedFlightMap';
import CalculationEngine, { BestRouteValue } from '../services/calculationEngine';
import FreeFlightAPIService from '../services/freeFlightAPIService';
import CurrencyService from '../services/currencyService';
import AmadeusService from '../services/amadeusService';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 12px 30px rgba(0, 0, 0, 0.1)'
      : '0 12px 30px rgba(0, 0, 0, 0.3)',
  },
}));



// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DealCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  background: theme.palette.mode === 'light'
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
  color: 'white',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

export const Airlines: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [airlinePrograms, setAirlinePrograms] = React.useState<LoyaltyProgram[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [programToDelete, setProgramToDelete] = React.useState<LoyaltyProgram | null>(null);
  const [bestRouteValue, setBestRouteValue] = React.useState<BestRouteValue | null>(null);
  const [dynamicDeals, setDynamicDeals] = React.useState<any[]>([]);
  const [transferOpportunities, setTransferOpportunities] = React.useState<any[]>([]);
  const [flightDetailsOpen, setFlightDetailsOpen] = React.useState(false);
  const [selectedDeal, setSelectedDeal] = React.useState<any>(null);

  // Flight search state
  const [searchOrigin, setSearchOrigin] = React.useState('');
  const [searchDestination, setSearchDestination] = React.useState('');
  const [searchDate, setSearchDate] = React.useState<Date | null>(new Date());
  const [searchClass, setSearchClass] = React.useState('economy');
  const [searchCurrency, setSearchCurrency] = React.useState('USD');
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState<any[]>([]);
  const [activeTab, setActiveTab] = React.useState(0);


  const loadDynamicStatistics = React.useCallback(async (programs: LoyaltyProgram[]) => {
    try {
      // Calculate best route value
      const bestRoute = await CalculationEngine.calculateBestRouteValue(programs);
      setBestRouteValue(bestRoute);

      // Generate personalized deals
      const deals = await CalculationEngine.generatePersonalizedDeals(programs);
      setDynamicDeals(deals);

      // Get all user's credit cards for transfer opportunities
      const allPrograms = user?.id ? await FirestoreService.getUserLoyaltyPrograms(user.id) : [];
      const creditCards = allPrograms.filter(p => p.type === 'credit_card');
      const transferOpps = CalculationEngine.calculateTransferOpportunities(creditCards, programs);
      setTransferOpportunities(transferOpps);
      
      console.log('✅ Dynamic statistics loaded');
    } catch (error) {
      console.error('Error loading dynamic statistics:', error);
    }
  }, [user]);

  React.useEffect(() => {
    const loadAirlinePrograms = async () => {
      try {
        // Check if user is logged in for Firestore operations
        if (!user?.id) {
          setAirlinePrograms([]);
          setBestRouteValue(null);
          setDynamicDeals([]);
          setTransferOpportunities([]);
          return;
        }

        // Use Firestore service for professional data loading
        const airlinePrograms = await FirestoreService.getUserLoyaltyProgramsByType(user.id, 'airline');
        setAirlinePrograms(airlinePrograms);
        
        // Load dynamic statistics based on user's programs
        await loadDynamicStatistics(airlinePrograms);
      } catch (error) {
        console.error('Error loading airline programs:', error);
        setAirlinePrograms([]);
      } finally {
        if (!authLoading) {
          setLoading(false);
        }
      }
    };

    // Only load data when auth state is determined
    if (!authLoading) {
      loadAirlinePrograms();
    }
  }, [user, authLoading, loadDynamicStatistics]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOpenFlightDetails = (deal: any) => {
    setSelectedDeal(deal);
    setFlightDetailsOpen(true);
  };

  const handleCloseFlightDetails = () => {
    setFlightDetailsOpen(false);
    setSelectedDeal(null);
  };

  // Delete airline program function (consistent with Cards page)
  const handleDeleteAirlineProgram = async (programToDelete: LoyaltyProgram) => {
    // Show confirmation dialog
    setProgramToDelete(programToDelete);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!user || !programToDelete) return;
    
    try {
      // Remove from Firestore
      await FirestoreService.deleteLoyaltyProgram(programToDelete.id);
      
      // Update local state
      setAirlinePrograms(prev => prev.filter(program => program.id !== programToDelete.id));
      console.log('Airline program deleted successfully:', programToDelete.name);
      
      // Close dialog and reset
      setDeleteConfirmOpen(false);
      setProgramToDelete(null);
    } catch (error) {
      console.error('Error deleting airline program:', error);
      alert(`Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Airline code mappings for flight numbers
  const AIRLINE_CODE_MAP: Record<string, string> = {
    'Emirates': 'EK',
    'IndiGo': '6E', 
    'SpiceJet': 'SG',
    'Air India': 'AI',
    'Vistara': 'UK',
    'Qatar Airways': 'QR'
  };

  // Airport data for search - Comprehensive list
  const airports = [
    // Major Indian Cities
    { code: 'DEL', name: 'Delhi', city: 'New Delhi', country: 'India' },
    { code: 'BOM', name: 'Mumbai', city: 'Mumbai', country: 'India' },
    { code: 'BLR', name: 'Bangalore', city: 'Bangalore', country: 'India' },
    { code: 'MAA', name: 'Chennai', city: 'Chennai', country: 'India' },
    { code: 'CCU', name: 'Kolkata', city: 'Kolkata', country: 'India' },
    { code: 'HYD', name: 'Hyderabad', city: 'Hyderabad', country: 'India' },
    { code: 'PNQ', name: 'Pune', city: 'Pune', country: 'India' },
    { code: 'AMD', name: 'Ahmedabad', city: 'Ahmedabad', country: 'India' },
    { code: 'JAI', name: 'Jaipur', city: 'Jaipur', country: 'India' },
    { code: 'LKO', name: 'Lucknow', city: 'Lucknow', country: 'India' },
    { code: 'BHU', name: 'Bhubaneswar', city: 'Bhubaneswar', country: 'India' },
    { code: 'GOI', name: 'Goa', city: 'Panaji', country: 'India' },
    { code: 'COK', name: 'Kochi', city: 'Kochi', country: 'India' },
    { code: 'TRV', name: 'Thiruvananthapuram', city: 'Trivandrum', country: 'India' },
    { code: 'CNN', name: 'Kannur', city: 'Kannur', country: 'India' },
    { code: 'IXR', name: 'Ranchi', city: 'Ranchi', country: 'India' },
    { code: 'PAT', name: 'Patna', city: 'Patna', country: 'India' },
    { code: 'VNS', name: 'Varanasi', city: 'Varanasi', country: 'India' },
    { code: 'IXC', name: 'Chandigarh', city: 'Chandigarh', country: 'India' },
    { code: 'SXR', name: 'Srinagar', city: 'Srinagar', country: 'India' },
    { code: 'LEH', name: 'Leh', city: 'Leh', country: 'India' },
    
    // Middle East
    { code: 'DXB', name: 'Dubai', city: 'Dubai', country: 'UAE' },
    { code: 'AUH', name: 'Abu Dhabi', city: 'Abu Dhabi', country: 'UAE' },
    { code: 'SHJ', name: 'Sharjah', city: 'Sharjah', country: 'UAE' },
    { code: 'DOH', name: 'Doha', city: 'Doha', country: 'Qatar' },
    { code: 'KWI', name: 'Kuwait', city: 'Kuwait City', country: 'Kuwait' },
    { code: 'BAH', name: 'Bahrain', city: 'Manama', country: 'Bahrain' },
    { code: 'MCT', name: 'Muscat', city: 'Muscat', country: 'Oman' },
    { code: 'RUH', name: 'Riyadh', city: 'Riyadh', country: 'Saudi Arabia' },
    { code: 'JED', name: 'Jeddah', city: 'Jeddah', country: 'Saudi Arabia' },
    
    // Southeast Asia
    { code: 'SIN', name: 'Singapore', city: 'Singapore', country: 'Singapore' },
    { code: 'BKK', name: 'Bangkok', city: 'Bangkok', country: 'Thailand' },
    { code: 'KUL', name: 'Kuala Lumpur', city: 'Kuala Lumpur', country: 'Malaysia' },
    { code: 'CGK', name: 'Jakarta', city: 'Jakarta', country: 'Indonesia' },
    { code: 'MNL', name: 'Manila', city: 'Manila', country: 'Philippines' },
    { code: 'HKG', name: 'Hong Kong', city: 'Hong Kong', country: 'Hong Kong' },
    { code: 'TPE', name: 'Taipei', city: 'Taipei', country: 'Taiwan' },
    { code: 'HAN', name: 'Hanoi', city: 'Hanoi', country: 'Vietnam' },
    { code: 'SGN', name: 'Ho Chi Minh City', city: 'Ho Chi Minh City', country: 'Vietnam' },
    
    // East Asia
    { code: 'NRT', name: 'Tokyo Narita', city: 'Tokyo', country: 'Japan' },
    { code: 'HND', name: 'Tokyo Haneda', city: 'Tokyo', country: 'Japan' },
    { code: 'KIX', name: 'Osaka', city: 'Osaka', country: 'Japan' },
    { code: 'ICN', name: 'Seoul Incheon', city: 'Seoul', country: 'South Korea' },
    { code: 'GMP', name: 'Seoul Gimpo', city: 'Seoul', country: 'South Korea' },
    { code: 'PEK', name: 'Beijing Capital', city: 'Beijing', country: 'China' },
    { code: 'PVG', name: 'Shanghai Pudong', city: 'Shanghai', country: 'China' },
    { code: 'CAN', name: 'Guangzhou', city: 'Guangzhou', country: 'China' },
    
    // Europe
    { code: 'LHR', name: 'London Heathrow', city: 'London', country: 'UK' },
    { code: 'LGW', name: 'London Gatwick', city: 'London', country: 'UK' },
    { code: 'CDG', name: 'Paris Charles de Gaulle', city: 'Paris', country: 'France' },
    { code: 'FRA', name: 'Frankfurt', city: 'Frankfurt', country: 'Germany' },
    { code: 'MUC', name: 'Munich', city: 'Munich', country: 'Germany' },
    { code: 'AMS', name: 'Amsterdam', city: 'Amsterdam', country: 'Netherlands' },
    { code: 'ZUR', name: 'Zurich', city: 'Zurich', country: 'Switzerland' },
    { code: 'VIE', name: 'Vienna', city: 'Vienna', country: 'Austria' },
    { code: 'FCO', name: 'Rome Fiumicino', city: 'Rome', country: 'Italy' },
    { code: 'MAD', name: 'Madrid', city: 'Madrid', country: 'Spain' },
    { code: 'BCN', name: 'Barcelona', city: 'Barcelona', country: 'Spain' },
    
    // North America
    { code: 'JFK', name: 'New York JFK', city: 'New York', country: 'USA' },
    { code: 'LGA', name: 'New York LaGuardia', city: 'New York', country: 'USA' },
    { code: 'EWR', name: 'Newark', city: 'New York/Newark', country: 'USA' },
    { code: 'LAX', name: 'Los Angeles', city: 'Los Angeles', country: 'USA' },
    { code: 'SFO', name: 'San Francisco', city: 'San Francisco', country: 'USA' },
    { code: 'ORD', name: 'Chicago O\'Hare', city: 'Chicago', country: 'USA' },
    { code: 'MIA', name: 'Miami', city: 'Miami', country: 'USA' },
    { code: 'DFW', name: 'Dallas Fort Worth', city: 'Dallas', country: 'USA' },
    { code: 'ATL', name: 'Atlanta', city: 'Atlanta', country: 'USA' },
    { code: 'SEA', name: 'Seattle', city: 'Seattle', country: 'USA' },
    { code: 'YYZ', name: 'Toronto Pearson', city: 'Toronto', country: 'Canada' },
    { code: 'YVR', name: 'Vancouver', city: 'Vancouver', country: 'Canada' },
    
    // Australia & Oceania
    { code: 'SYD', name: 'Sydney', city: 'Sydney', country: 'Australia' },
    { code: 'MEL', name: 'Melbourne', city: 'Melbourne', country: 'Australia' },
    { code: 'BNE', name: 'Brisbane', city: 'Brisbane', country: 'Australia' },
    { code: 'PER', name: 'Perth', city: 'Perth', country: 'Australia' },
    { code: 'AKL', name: 'Auckland', city: 'Auckland', country: 'New Zealand' },
    
    // Africa
    { code: 'JNB', name: 'Johannesburg', city: 'Johannesburg', country: 'South Africa' },
    { code: 'CAI', name: 'Cairo', city: 'Cairo', country: 'Egypt' },
    { code: 'ADD', name: 'Addis Ababa', city: 'Addis Ababa', country: 'Ethiopia' },
    { code: 'NBO', name: 'Nairobi', city: 'Nairobi', country: 'Kenya' }
  ];

  const cabinClasses = [
    { value: 'economy', label: 'Economy' },
    { value: 'premium_economy', label: 'Premium Economy' },
    { value: 'business', label: 'Business' },
    { value: 'first', label: 'First Class' }
  ];

  // Search flights functionality
  const searchFlights = async () => {
    if (!searchOrigin || !searchDestination) {
      return;
    }

    setSearchLoading(true);
    try {
      let amadeusResults: any[] = [];
      let liveFlightData: any[] = [];

      // First, try to get real-time data from Amadeus API
      if (AmadeusService.isConfigured()) {
        try {
          console.log('🎯 Searching with Amadeus API for real-time data...');
          console.log(`Search parameters: ${searchOrigin} → ${searchDestination}, Date: ${searchDate?.toISOString().split('T')[0]}, Class: ${searchClass}`);
          
          const amadeusOffers = await AmadeusService.searchFlightOffers({
            originLocationCode: searchOrigin,
            destinationLocationCode: searchDestination,
            departureDate: searchDate ? searchDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            adults: 1,
            travelClass: searchClass.toUpperCase() as any,
            currencyCode: searchCurrency,
            max: 10
          });
          
          if (amadeusOffers.length > 0) {
            console.log(`✅ Found ${amadeusOffers.length} live flight offers from Amadeus`);
            console.log('Sample Amadeus offer:', amadeusOffers[0]);
            amadeusResults = amadeusOffers;
          } else {
            console.warn('⚠️ Amadeus returned 0 results');
          }
        } catch (error) {
          console.error('❌ Amadeus API error:', error);
        }
      } else {
        console.warn('⚠️ Amadeus API not configured');
      }

      // Also get live flight tracking data from OpenSky
      try {
        const liveFlights = await FreeFlightAPIService.getLiveFlights();
        liveFlightData = liveFlights.slice(0, 10);
      } catch (error) {
        // Silently fall back if OpenSky is unavailable
      }

      // Get route-specific search from AviationStack
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const routeResults = await FreeFlightAPIService.searchFlightsByRoute(
        searchOrigin, 
        searchDestination, 
        searchDate ? searchDate.toISOString().split('T')[0] : undefined
      );

      // Convert to our deal format with results for ALL user loyalty programs
      const searchDeals: any[] = [];
      
      // Process real-time Amadeus data if available
      if (amadeusResults.length > 0 && airlinePrograms.length > 0) {
        console.log('💱 Processing live flight data with currency conversion...');
        
        for (const amadeus of amadeusResults.slice(0, airlinePrograms.length)) {
          for (let programIndex = 0; programIndex < airlinePrograms.length; programIndex++) {
            const program = airlinePrograms[programIndex];
            // Estimate points cost based on cash price
            const cashPrice = amadeus.price.total;
            const estimatedPoints = Math.round(cashPrice * 600); // Rough estimate: $1 = 600 points
            
            // Apply program-specific multipliers
            const programMultiplier = program.name.toLowerCase().includes('delta') ? 0.9 :
                                     program.name.toLowerCase().includes('united') ? 1.0 :
                                     program.name.toLowerCase().includes('american') ? 1.1 :
                                     program.name.toLowerCase().includes('southwest') ? 0.8 : 1.0;
            
            const finalPoints = Math.round(estimatedPoints * programMultiplier);
            
            // Convert cash portion to user's selected currency
            let convertedCash = cashPrice;
            let cashCurrency = amadeus.price.currency;
            let formattedCash = `${cashCurrency} ${cashPrice.toFixed(2)}`;
            
            if (searchCurrency !== amadeus.price.currency) {
              try {
                const conversion = await CurrencyService.convertCurrency(
                  cashPrice,
                  amadeus.price.currency,
                  searchCurrency
                );
                convertedCash = conversion.converted.amount;
                cashCurrency = searchCurrency;
                formattedCash = conversion.converted.formatted;
              } catch (error) {
                console.warn('Currency conversion failed, using original currency');
              }
            }
            
            searchDeals.push({
              route: `${searchOrigin} → ${searchDestination}`,
              class: amadeus.cabin.charAt(0).toUpperCase() + amadeus.cabin.slice(1).toLowerCase(),
              airline: program.name,
              operatingAirline: amadeus.airline,
              points: finalPoints,
              cash: Math.round(convertedCash),
              cashCurrency: cashCurrency,
              formattedCash: formattedCash,
              savings: `${Math.round(15 + Math.random() * 25)}%`,
              canAfford: program.points >= finalPoints,
              recommendationReason: program.points >= finalPoints 
                ? `Use your ${program.name} points • Real-time pricing`
                : `${program.name} option (need ${Math.max(0, finalPoints - program.points).toLocaleString()} more points)`,
              flightNumber: amadeus.flightNumber,
              duration: amadeus.duration,
              departure: amadeus.departure.time,
              arrival: amadeus.arrival.time,
              isLiveData: true,
              isAmadeus: true,
              availability: amadeus.availability,
              programId: program.id
            });
          }
        }
      } 
      
      // If we don't have enough Amadeus results or no loyalty programs, use fallback data
      if (searchDeals.length < 6) {
        // Generate additional results using existing logic with currency conversion
        const remainingSlots = 6 - searchDeals.length;
        const programsToUse = airlinePrograms.length > 0 ? airlinePrograms : [
          { name: 'Delta SkyMiles', points: 0, color: '#003366' },
          { name: 'United MileagePlus', points: 0, color: '#0e4194' },
          { name: 'American AAdvantage', points: 0, color: '#c41e3a' }
        ];
        
        for (let i = 0; i < Math.min(remainingSlots, programsToUse.length * 2); i++) {
          const program = programsToUse[i % programsToUse.length];
          const liveFlight = liveFlightData[i % liveFlightData.length];
          const hasLiveData = liveFlight && liveFlight.callsign;
          
          // Calculate points based on distance, class, and program-specific rates
          const basePoints = searchClass === 'economy' ? 12000 : 
                            searchClass === 'premium_economy' ? 18000 :
                            searchClass === 'business' ? 35000 : 65000;
          
          const routeMultiplier = getRouteMultiplier(searchOrigin, searchDestination);
          const programMultiplier = program.name.toLowerCase().includes('delta') ? 0.9 :
                                   program.name.toLowerCase().includes('united') ? 1.0 :
                                   program.name.toLowerCase().includes('american') ? 1.1 :
                                   program.name.toLowerCase().includes('southwest') ? 0.8 : 1.0;
          
          const dynamicPoints = Math.round(basePoints * routeMultiplier * programMultiplier * (0.85 + Math.random() * 0.3));
          
          // Calculate cash price and convert currency
          const baseCashUSD = Math.round(dynamicPoints * 0.006); // Rough estimate: point = $0.006
          let convertedCash = baseCashUSD;
          let formattedCash = CurrencyService.formatCurrency(baseCashUSD, 'USD');
          
          if (searchCurrency !== 'USD') {
            try {
              const conversion = await CurrencyService.convertCurrency(baseCashUSD, 'USD', searchCurrency);
              convertedCash = Math.round(conversion.converted.amount);
              formattedCash = conversion.converted.formatted;
            } catch (error) {
              // Use fallback conversion
            }
          }
          
          const operatingAirlines = ['Emirates', 'IndiGo', 'SpiceJet', 'Air India', 'Vistara', 'Qatar Airways'];
          const operatingAirline = operatingAirlines[i % operatingAirlines.length];
          
          searchDeals.push({
            route: `${searchOrigin} → ${searchDestination}`,
            class: searchClass === 'economy' ? 'Economy' : 
                   searchClass === 'premium_economy' ? 'Premium Economy' :
                   searchClass === 'business' ? 'Business' : 'First Class',
            airline: program.name,
            operatingAirline: operatingAirline,
            points: dynamicPoints,
            cash: convertedCash,
            cashCurrency: searchCurrency,
            formattedCash: formattedCash,
            savings: `${Math.round(20 + Math.random() * 35)}%`,
            canAfford: (program as any).points >= dynamicPoints,
            recommendationReason: (program as any).points >= dynamicPoints 
              ? `Use your ${program.name} points • ${searchClass} class`
              : `${program.name} option (need ${Math.max(0, dynamicPoints - (program as any).points).toLocaleString()} more points)`,
            flightNumber: hasLiveData ? liveFlight.callsign : `EK${200 + i}`, // Use Emirates as default
            duration: calculateFlightDuration(searchOrigin, searchDestination),
            departure: new Date(Date.now() + (i * 30 * 60 * 1000)).toISOString(),
            arrival: new Date(Date.now() + (i * 30 * 60 * 1000) + (3 * 60 * 60 * 1000)).toISOString(),
            isLiveData: hasLiveData,
            livePosition: hasLiveData ? { 
              lat: liveFlight.latitude, 
              lon: liveFlight.longitude,
              altitude: liveFlight.altitude,
              velocity: liveFlight.velocity 
            } : null,
            programId: (program as any).id || `${program.name.replace(/\s+/g, '_').toLowerCase()}`
          });
        }
      }

      setSearchResults(searchDeals);
      setDynamicDeals(searchDeals); // Update the main deals display
      
    } catch (error) {
      // Fallback to enhanced search results
      generateFallbackSearchResults();
    } finally {
      setSearchLoading(false);
    }
  };

  // Helper functions for dynamic calculations
  const getRouteMultiplier = (origin: string, destination: string): number => {
    const distances: Record<string, number> = {
      'BOM-DXB': 1.0, 'DEL-DXB': 1.1, 'BLR-SIN': 1.2,
      'BOM-LHR': 2.8, 'DEL-LHR': 2.9, 'BOM-JFK': 4.2,
      'BOM-SIN': 1.3, 'DEL-NRT': 2.1, 'BLR-BKK': 0.9
    };
    return distances[`${origin}-${destination}`] || distances[`${destination}-${origin}`] || 1.5;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const extractAirlineFromCallsign = (callsign: string): string => {
    const airlines: Record<string, string> = {
      'AI': 'Air India', 'UK': 'Vistara', '6E': 'IndiGo',
      'EK': 'Emirates', 'SQ': 'Singapore Airlines', 'BA': 'British Airways'
    };
    const code = callsign.substring(0, 2).toUpperCase();
    return airlines[code] || 'Live Flight';
  };

  const calculateFlightDuration = (origin: string, destination: string): string => {
    const durations: Record<string, string> = {
      'BOM-DXB': '3h 15m', 'DEL-DXB': '3h 30m', 'BLR-SIN': '4h 20m',
      'BOM-LHR': '9h 30m', 'DEL-LHR': '9h 15m', 'BOM-JFK': '16h 30m'
    };
    return durations[`${origin}-${destination}`] || durations[`${destination}-${origin}`] || '4h 30m';
  };

  const generateFallbackSearchResults = () => {
    const fallbackDeals: any[] = [];
    
    // If user has loyalty programs, show results for each program
    if (airlinePrograms.length > 0) {
      airlinePrograms.forEach((program, programIndex) => {
        // Calculate points based on route, class, and program-specific rates
        const basePoints = searchClass === 'economy' ? 12000 : 
                          searchClass === 'premium_economy' ? 18000 :
                          searchClass === 'business' ? 35000 : 65000;
        const routeMultiplier = getRouteMultiplier(searchOrigin, searchDestination);
        
        // Add program-specific multipliers for different pricing strategies
        const programMultiplier = program.name.toLowerCase().includes('delta') ? 0.9 :
                                 program.name.toLowerCase().includes('united') ? 1.0 :
                                 program.name.toLowerCase().includes('american') ? 1.1 :
                                 program.name.toLowerCase().includes('southwest') ? 0.8 :
                                 1.0; // Default multiplier
        
        const dynamicPoints = Math.round(basePoints * routeMultiplier * programMultiplier * (0.85 + Math.random() * 0.3));
        
        // Get operating airline for this result with proper IATA codes
        const operatingAirlines = ['Emirates', 'IndiGo', 'SpiceJet', 'Air India', 'Vistara', 'Qatar Airways'];
        const operatingAirline = operatingAirlines[programIndex % operatingAirlines.length];
        
        // Use proper IATA codes for flight numbers
        const airlineCode = AIRLINE_CODE_MAP[operatingAirline] || 'EK';

        fallbackDeals.push({
          route: `${searchOrigin} → ${searchDestination}`,
          class: searchClass === 'economy' ? 'Economy' : 
                 searchClass === 'premium_economy' ? 'Premium Economy' :
                 searchClass === 'business' ? 'Business' : 'First Class',
          airline: program.name, // Show the specific loyalty program
          operatingAirline: operatingAirline,
          points: dynamicPoints,
          cash: Math.round(dynamicPoints * 0.6 * (0.8 + Math.random() * 0.4)),
          savings: `${Math.round(20 + Math.random() * 40)}%`,
          canAfford: program.points >= dynamicPoints,
          recommendationReason: program.points >= dynamicPoints 
            ? `Use your ${program.name} points • ${searchClass} class`
            : `${program.name} option (need ${Math.max(0, dynamicPoints - program.points).toLocaleString()} more points)`,
          flightNumber: `${airlineCode}${200 + programIndex}`,
          duration: calculateFlightDuration(searchOrigin, searchDestination),
          departure: new Date(Date.now() + (programIndex * 30 * 60 * 1000)).toISOString(),
          arrival: new Date(Date.now() + (programIndex * 30 * 60 * 1000) + (3 * 60 * 60 * 1000)).toISOString(),
          programId: program.id
        });
      });
      
      // Add some additional flight options with partner airlines for variety
      const additionalFlights = Math.max(0, 6 - fallbackDeals.length);
      for (let i = 0; i < additionalFlights; i++) {
        const randomProgram = airlinePrograms[i % airlinePrograms.length];
        const basePoints = searchClass === 'economy' ? 12000 : 
                          searchClass === 'premium_economy' ? 18000 :
                          searchClass === 'business' ? 35000 : 65000;
        const routeMultiplier = getRouteMultiplier(searchOrigin, searchDestination);
        const dynamicPoints = Math.round(basePoints * routeMultiplier * (1.1 + Math.random() * 0.4)); // Partner flights cost more
        
        const partnerAirlines = ['Lufthansa', 'Thai Airways', 'Singapore Airlines', 'Cathay Pacific'];
        const partnerAirline = partnerAirlines[i % partnerAirlines.length];
        
        fallbackDeals.push({
          route: `${searchOrigin} → ${searchDestination}`,
          class: searchClass === 'economy' ? 'Economy' : 
                 searchClass === 'premium_economy' ? 'Premium Economy' :
                 searchClass === 'business' ? 'Business' : 'First Class',
          airline: randomProgram.name,
          operatingAirline: partnerAirline,
          points: dynamicPoints,
          cash: Math.round(dynamicPoints * 0.6 * (0.8 + Math.random() * 0.4)),
          savings: `${Math.round(15 + Math.random() * 25)}%`,
          canAfford: randomProgram.points >= dynamicPoints,
          recommendationReason: randomProgram.points >= dynamicPoints 
            ? `${randomProgram.name} partner flight • ${searchClass} class`
            : `${randomProgram.name} partner (need ${Math.max(0, dynamicPoints - randomProgram.points).toLocaleString()} more points)`,
          flightNumber: `${partnerAirline.substring(0, 2).toUpperCase()}${300 + i}`,
          duration: calculateFlightDuration(searchOrigin, searchDestination),
          departure: new Date(Date.now() + ((airlinePrograms.length + i) * 45 * 60 * 1000)).toISOString(),
          arrival: new Date(Date.now() + ((airlinePrograms.length + i) * 45 * 60 * 1000) + (3 * 60 * 60 * 1000)).toISOString(),
          programId: randomProgram.id,
          isPartner: true
        });
      }
    } else {
      // If no programs exist, show generic results encouraging user to add programs
      const airlines = ['Delta SkyMiles', 'United MileagePlus', 'American AAdvantage'];
      const operatingAirlines = ['Emirates', 'IndiGo', 'SpiceJet', 'Air India', 'Vistara', 'Qatar Airways'];
      
      for (let i = 0; i < 6; i++) {
        const basePoints = searchClass === 'economy' ? 12000 : 
                          searchClass === 'premium_economy' ? 18000 :
                          searchClass === 'business' ? 35000 : 65000;
        const routeMultiplier = getRouteMultiplier(searchOrigin, searchDestination);
        const dynamicPoints = Math.round(basePoints * routeMultiplier * (0.8 + Math.random() * 0.4));

        fallbackDeals.push({
          route: `${searchOrigin} → ${searchDestination}`,
          class: searchClass === 'economy' ? 'Economy' : 
                 searchClass === 'premium_economy' ? 'Premium Economy' :
                 searchClass === 'business' ? 'Business' : 'First Class',
          airline: airlines[i % airlines.length],
          operatingAirline: operatingAirlines[i],
          points: dynamicPoints,
          cash: Math.round(dynamicPoints * 0.6 * (0.8 + Math.random() * 0.4)),
          savings: `${Math.round(20 + Math.random() * 40)}%`,
          canAfford: false,
          recommendationReason: `Add ${airlines[i % airlines.length]} to book this flight`,
          flightNumber: `${operatingAirlines[i].substring(0, 2).toUpperCase()}${200 + i}`,
          duration: calculateFlightDuration(searchOrigin, searchDestination),
          departure: new Date(Date.now() + (i * 30 * 60 * 1000)).toISOString(),
          arrival: new Date(Date.now() + (i * 30 * 60 * 1000) + (3 * 60 * 60 * 1000)).toISOString()
        });
      }
    }

    setSearchResults(fallbackDeals);
    setDynamicDeals(fallbackDeals);
  };

  const swapAirports = () => {
    const temp = searchOrigin;
    setSearchOrigin(searchDestination);
    setSearchDestination(temp);
  };

  const totalMiles = airlinePrograms.reduce((sum: number, program: LoyaltyProgram) => sum + program.points, 0);
  const activePrograms = airlinePrograms.length;

  const handleAddProgram = async (newProgramData: Omit<LoyaltyProgram, 'id'>) => {
    if (!user?.id) {
      alert('You must be logged in to add airline programs');
      return;
    }
    
    try {
      const savedProgram = await FirestoreService.addLoyaltyProgram(user.id, newProgramData);
      const updatedPrograms = [...airlinePrograms, savedProgram];
      setAirlinePrograms(updatedPrograms);
      
      // Refresh dynamic statistics with new program
      await loadDynamicStatistics(updatedPrograms);
      
      setAddDialogOpen(false);
    } catch (error) {
      console.error('Error adding airline program:', error);
      alert(`Failed to add airline program: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteConfirm = async (program: LoyaltyProgram) => {
    if (!user?.id) {
      alert('You must be logged in to delete airline programs');
      return;
    }

    try {
      await FirestoreService.deleteLoyaltyProgram(program.id);
      const updatedPrograms = airlinePrograms.filter(p => p.id !== program.id);
      setAirlinePrograms(updatedPrograms);
      
      // Refresh dynamic statistics after deletion
      await loadDynamicStatistics(updatedPrograms);
    } catch (error) {
      console.error('Error deleting airline program:', error);
      alert(`Failed to delete airline program: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };



  // Dynamic data is now loaded via state variables:
  // - dynamicDeals: Generated personalized flight deals based on user's programs
  // - dynamicTransfers: Real transfer opportunities from user's credit cards

  // Fallback data for when user has no programs yet
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fallbackTransfers = [
    { from: 'HDFC Infinia', to: 'Emirates', ratio: '1:1', maxTransfer: 100000, potentialValue: 150000 },
    { from: 'Axis Magnus', to: 'Air India', ratio: '1:1', maxTransfer: 80000, potentialValue: 120000 },
    { from: 'Chase Sapphire', to: 'Singapore Airlines', ratio: '1:1', maxTransfer: 150000, potentialValue: 200000 },
  ];

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Box sx={{ textAlign: 'center' }}>
            <LinearProgress sx={{ width: 200, mb: 2 }} />
            <Typography>Loading airline programs...</Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  // Tab content renderers
  function renderProgramsTab() {
    return (
      <>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Your Airline Programs
          </Typography>
        </Box>

        {/* Programs Grid - Enhanced Cards */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)', 
            lg: 'repeat(4, 1fr)' 
          },
          gap: 2,
          mb: 4
        }}>
          {airlinePrograms.map((program) => (
            <StyledCard
              key={program.id}
              sx={{
                background: (theme) => theme.palette.mode === 'dark'
                  ? `linear-gradient(135deg, 
                      rgba(255, 255, 255, 0.08) 0%, 
                      rgba(255, 255, 255, 0.04) 25%, 
                      rgba(255, 255, 255, 0.02) 50%, 
                      rgba(255, 255, 255, 0.04) 75%, 
                      rgba(255, 255, 255, 0.08) 100%), 
                      linear-gradient(135deg, 
                      ${program.color || '#0066cc'}60 0%, 
                      ${program.color || '#0066cc'}40 25%, 
                      ${program.color || '#0066cc'}30 50%, 
                      ${program.color || '#0066cc'}40 75%, 
                      ${program.color || '#0066cc'}60 100%)`
                  : `linear-gradient(135deg, 
                      ${program.color || '#0066cc'}20 0%, 
                      ${program.color || '#0066cc'}10 25%, 
                      ${program.color || '#0066cc'}05 50%, 
                      ${program.color || '#0066cc'}10 75%, 
                      ${program.color || '#0066cc'}20 100%)`,
                backdropFilter: 'blur(20px)',
                border: (theme) => theme.palette.mode === 'dark' 
                  ? `2px solid ${program.color || '#0066cc'}80`
                  : `1px solid ${program.color || '#0066cc'}30`,
                boxShadow: (theme) => theme.palette.mode === 'dark'
                  ? `0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)`
                  : 'none',
                color: 'text.primary',
                position: 'relative',
                overflow: 'hidden',
                minHeight: '160px',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: (theme) => theme.palette.mode === 'dark'
                    ? `0 8px 30px rgba(0, 0, 0, 0.4), 0 0 20px ${program.color || '#0066cc'}40, inset 0 1px 0 rgba(255, 255, 255, 0.15)`
                    : `0 8px 25px -4px ${program.color || '#0066cc'}40`,
                  border: (theme) => theme.palette.mode === 'dark'
                    ? `2px solid ${program.color || '#0066cc'}`
                    : `1px solid ${program.color || '#0066cc'}50`,
                  '& .delete-button': {
                    opacity: 1,
                  },
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-50%',
                  right: '-30%',
                  width: '120px',
                  height: '120px',
                  background: (theme) => theme.palette.mode === 'dark'
                    ? `radial-gradient(circle, ${program.color || '#0066cc'}40 0%, transparent 70%)`
                    : `radial-gradient(circle, ${program.color || '#0066cc'}15 0%, transparent 70%)`,
                  borderRadius: '50%',
                  filter: 'blur(10px)'
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-30%',
                  left: '-20%',
                  width: '80px',
                  height: '80px',
                  background: (theme) => theme.palette.mode === 'dark'
                    ? `radial-gradient(circle, ${program.color || '#0066cc'}30 0%, transparent 60%)`
                    : `radial-gradient(circle, ${program.color || '#0066cc'}10 0%, transparent 60%)`,
                  borderRadius: '50%',
                  filter: 'blur(8px)'
                }
              }}
            >
              <CardContent sx={{ 
                position: 'relative', 
                zIndex: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: 2
              }}>
                {/* Delete Button */}
                <IconButton
                  className="delete-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteAirlineProgram(program);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 6,
                    width: 28,
                    height: 28,
                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.15)' 
                      : 'rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: (theme) => theme.palette.mode === 'dark' 
                      ? '1px solid rgba(255, 255, 255, 0.4)' 
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    color: (theme) => theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.8)' 
                      : 'text.secondary',
                    opacity: 0,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(239, 68, 68, 0.2)',
                      color: 'error.main',
                      transform: 'scale(1.1)',
                      border: '1px solid rgba(239, 68, 68, 0.5)',
                    },
                  }}
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>

                {/* Header with Logo */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: (theme) => theme.palette.mode === 'dark' 
                      ? '#ffffff' 
                      : 'text.primary',
                    textShadow: (theme) => theme.palette.mode === 'dark'
                      ? '0 1px 2px rgba(0,0,0,0.7)'
                      : 'none'
                  }}>
                    {program.name}
                  </Typography>
                  <Box sx={{
                    background: (theme) => theme.palette.mode === 'dark'
                      ? `linear-gradient(135deg, 
                          rgba(255, 255, 255, 0.15) 0%, 
                          rgba(255, 255, 255, 0.05) 100%), 
                          linear-gradient(135deg, 
                          ${program.color || '#0066cc'}70, 
                          ${program.color || '#0066cc'}90)`
                      : `linear-gradient(135deg, ${program.color || '#0066cc'}20, ${program.color || '#0066cc'}40)`,
                    backdropFilter: 'blur(10px)',
                    border: (theme) => theme.palette.mode === 'dark'
                      ? `1px solid ${program.color || '#0066cc'}`
                      : `1px solid ${program.color || '#0066cc'}50`,
                    borderRadius: '50%',
                    p: 0.8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: (theme) => theme.palette.mode === 'dark'
                      ? `0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)`
                      : 'none'
                  }}>
                    <Flight sx={{ 
                      color: (theme) => theme.palette.mode === 'dark' 
                        ? '#ffffff' 
                        : program.color || '#0066cc', 
                      fontSize: 16 
                    }} />
                  </Box>
                </Box>

                {/* Tier Badge */}
                <Box sx={{ mb: 1 }}>
                  <Chip 
                    label={program.tier} 
                    size="small"
                    sx={{ 
                      backgroundColor: (theme) => theme.palette.mode === 'dark'
                        ? `rgba(255, 255, 255, 0.12)`
                        : `${program.color || '#0066cc'}20`,
                      backdropFilter: 'blur(10px)',
                      border: (theme) => theme.palette.mode === 'dark'
                        ? `1px solid rgba(255, 255, 255, 0.25)`
                        : `1px solid ${program.color || '#0066cc'}30`,
                      color: (theme) => theme.palette.mode === 'dark'
                        ? '#ffffff'
                        : program.color || '#0066cc',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: '22px',
                      boxShadow: (theme) => theme.palette.mode === 'dark'
                        ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                        : 'none',
                      '& .MuiChip-label': {
                        color: (theme) => theme.palette.mode === 'dark'
                          ? '#ffffff'
                          : program.color || '#0066cc'
                      }
                    }}
                  />
                </Box>

                {/* Points/Miles */}
                <Typography variant="h5" sx={{ 
                  fontWeight: 800,
                  fontSize: '1.3rem',
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? '#ffffff' 
                    : 'text.primary',
                  textShadow: (theme) => theme.palette.mode === 'dark'
                    ? '0 1px 2px rgba(0,0,0,0.7), 0 0 8px rgba(0,0,0,0.3)'
                    : '0 1px 2px rgba(0,0,0,0.1)'
                }}>
                  {program.points?.toLocaleString() || '0'}
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontSize: '0.8rem',
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.8)' 
                    : 'text.secondary',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textShadow: (theme) => theme.palette.mode === 'dark'
                    ? '0 1px 2px rgba(0,0,0,0.5)'
                    : 'none'
                }}>
                  {program.currency}
                </Typography>
              </CardContent>
            </StyledCard>
          ))}
        </Box>

        {airlinePrograms.length === 0 && (
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Flight sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                No Airline Programs Added
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Start tracking your airline miles by adding your loyalty programs
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAddDialogOpen(true)}
                size="large"
                sx={{ borderRadius: 2 }}
              >
                Add Your First Program
              </Button>
            </CardContent>
          </StyledCard>
        )}
      </>
    );
  }

  function renderFlightSearchTab() {
    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Search color="primary" />
          Flight Search & Deals
        </Typography>
        
        {/* Flight Search Form */}
        <StyledCard sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Search Award Flights</Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(6, 1fr) auto' },
              gap: 2,
              alignItems: 'end'
            }}>
              <Autocomplete
                value={airports.find(a => a.code === searchOrigin) || null}
                onChange={(_, newValue) => setSearchOrigin(newValue?.code || '')}
                options={airports}
                getOptionLabel={(option) => `${option.code} - ${option.name}`}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.code} - {option.city}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.name}, {option.country}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="From"
                    placeholder="Select origin"
                    variant="outlined"
                  />
                )}
              />

              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '56px' }}>
                <Button
                  onClick={swapAirports}
                  sx={{ minWidth: 'auto', p: 1, borderRadius: '50%' }}
                  disabled={!searchOrigin && !searchDestination}
                >
                  <SwapHoriz />
                </Button>
              </Box>

              <Autocomplete
                value={airports.find(a => a.code === searchDestination) || null}
                onChange={(_, newValue) => setSearchDestination(newValue?.code || '')}
                options={airports}
                getOptionLabel={(option) => `${option.code} - ${option.name}`}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.code} - {option.city}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.name}, {option.country}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="To"
                    placeholder="Select destination"
                    variant="outlined"
                  />
                )}
              />

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Departure Date"
                  value={searchDate}
                  onChange={(newValue: Date | null) => setSearchDate(newValue)}
                  minDate={new Date()}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>

              <FormControl>
                <InputLabel>Class</InputLabel>
                <Select
                  value={searchClass}
                  onChange={(e) => setSearchClass(e.target.value)}
                  label="Class"
                >
                  {cabinClasses.map((cls) => (
                    <MenuItem key={cls.value} value={cls.value}>
                      {cls.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={searchCurrency}
                  onChange={(e) => setSearchCurrency(e.target.value)}
                  label="Currency"
                >
                  {CurrencyService.SUPPORTED_CURRENCIES.map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {currency.symbol}
                        </Typography>
                        <Typography variant="body2">
                          {currency.code}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={searchFlights}
                disabled={!searchOrigin || !searchDestination || searchLoading}
                startIcon={searchLoading ? <CircularProgress size={20} /> : <Search />}
                sx={{ 
                  height: '56px',
                  minWidth: '120px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  }
                }}
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </Box>
            
            {searchResults.length > 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Found {searchResults.length} flights for {searchOrigin} → {searchDestination}
                {searchDate && ` on ${format(searchDate, 'dd/MM/yyyy')}`}
              </Alert>
            )}
          </CardContent>
        </StyledCard>

        {/* Personalized Flight Deals */}
        {dynamicDeals.length > 0 && renderDynamicDeals()}
        
        {dynamicDeals.length === 0 && (
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Flight sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                No Personalized Deals Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add airline programs to see personalized flight deals based on your points balance
              </Typography>
            </CardContent>
          </StyledCard>
        )}
      </Box>
    );
  }

  function renderFlightMapTab() {
    const userAirlines = Array.from(new Set(airlinePrograms.map(program => 
      program.name.replace(/\s+(Airlines?|Airways?)/i, '').trim()
    ))); // Remove duplicates with Set

    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Map color="primary" />
          Live Flight Tracking
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Track live flights from airlines in your loyalty programs and discover earning opportunities.
        </Typography>

        {userAirlines.length > 0 && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              🎯 <strong>Smart Filtering Available:</strong> Toggle "My Airlines Only" to see flights from: {userAirlines.join(', ')}
            </Typography>
          </Alert>
        )}
        
        <EmbeddedFlightMap 
          height={500}
          userAirlines={userAirlines}
          showControls={true}
          title="Live Flight Tracking with Points Earning Opportunities"
        />
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            💡 How to Maximize Points Earning
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2
          }}>
            <StyledCard>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Track Your Airlines
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use the "My Airlines Only" filter to see live flights from airlines where you have loyalty programs.
                </Typography>
              </CardContent>
            </StyledCard>
            
            <StyledCard>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Route Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click on flights to see route details and get personalized credit card recommendations.
                </Typography>
              </CardContent>
            </StyledCard>
            
            <StyledCard>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Domestic vs International
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Different earning rates apply for domestic and international flights - plan accordingly!
                </Typography>
              </CardContent>
            </StyledCard>
          </Box>
        </Box>
      </Box>
    );
  }

  // Helper functions for dynamic content
  function renderDynamicDeals() {
    if (dynamicDeals.length === 0) return null;

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
          Personalized Flight Deals
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(300px, 1fr))' },
          gap: 3
        }}>
          {dynamicDeals.slice(0, 6).map((deal, index) => (
            <StyledCard key={index}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {deal.route || 'Unknown Route'}
                  </Typography>
                  <Chip
                    label={deal.class || 'Economy'}
                    color="primary"
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {deal.airline || 'Unknown'} • {deal.operatingAirline || 'Unknown'}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {deal.points?.toLocaleString() || 'N/A'} points
                    </Typography>
                    {deal.formattedCash && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          + {deal.formattedCash} cash
                        </Typography>
                        {deal.isAmadeus && (
                          <Chip 
                            label="Live Price" 
                            size="small" 
                            color="success" 
                            sx={{ fontSize: '0.7rem' }} 
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: deal.canAfford ? 'success.main' : 'warning.main',
                    fontWeight: 600
                  }}>
                    {deal.canAfford ? '✅ Available' : '⚠️ Need more'}
                  </Typography>
                </Box>
                
                <Button
                  variant={deal.canAfford ? 'contained' : 'outlined'}
                  fullWidth
                  onClick={() => {
                    setSelectedDeal(deal);
                    setFlightDetailsOpen(true);
                  }}
                  size="small"
                >
                  {deal.canAfford ? 'View Details' : 'See Requirements'}
                </Button>
              </CardContent>
            </StyledCard>
          ))}
        </Box>
      </Box>
    );
  }

  function renderTransferOpportunities() {
    if (transferOpportunities.length === 0) return null;

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
          Transfer Opportunities
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Maximize your points by transferring from credit cards to airline programs
        </Typography>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(280px, 1fr))' },
          gap: 3
        }}>
          {transferOpportunities.slice(0, 4).map((opp, index) => (
            <StyledCard key={index}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {opp.from} → {opp.to}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                  {opp.ratio} transfer ratio
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Transfer up to {opp.maxTransfer?.toLocaleString() || 'N/A'} points
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600,
                  color: 'primary.main'
                }}>
                  Potential value: ₹{opp.potentialValue?.toLocaleString() || 'N/A'}
                </Typography>
              </CardContent>
            </StyledCard>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: 'text.primary' }}>
          Airline Miles Tracker
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
          Manage your airline loyalty programs and discover the best redemption opportunities
        </Typography>

        {/* Summary Cards */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 4 
        }}>
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Flight sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {(totalMiles || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Airline Miles
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Across all programs
              </Typography>
            </CardContent>
          </StyledCard>
          
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Star sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {activePrograms}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Programs
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Connected accounts
              </Typography>
            </CardContent>
          </StyledCard>
          
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <TrendingUp sx={{ fontSize: 48, color: '#10b981', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {bestRouteValue ? `${bestRouteValue.valuePerPointINR}₹/point` : 'Loading...'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Best Route Value
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {bestRouteValue ? bestRouteValue.route : 'Calculating...'}
              </Typography>
            </CardContent>
          </StyledCard>
          
          <StyledCard 
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: (theme) => theme.shadows[8]
              }
            }}
            onClick={() => setAddDialogOpen(true)} // Open add program dialog
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Add sx={{ fontSize: 48, color: 'white', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Add Program
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Connect new airline
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }} display="block">
                Track more miles
              </Typography>
            </CardContent>
          </StyledCard>
        </Box>
      </Box>



      {/* Tab Navigation */}
      <Box sx={{ mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab 
            label="My Programs" 
            icon={<Analytics />} 
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }} 
          />
          <Tab 
            label="Flight Search" 
            icon={<Search />} 
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }} 
          />
          <Tab 
            label="Live Flight Map" 
            icon={<Map />} 
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }} 
          />
          <Tab 
            label="Transfer Options" 
            icon={<TrendingUp />} 
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }} 
          />
        </Tabs>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Box>
            {/* My Programs Content */}
            {renderProgramsTab()}
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            {/* Flight Search Content */}
            {renderFlightSearchTab()}
          </Box>
        )}

        {activeTab === 2 && (
          <Box>
            {/* Live Flight Map Content */}
            {renderFlightMapTab()}
          </Box>
        )}

        {activeTab === 3 && (
          <Box>
            {/* Transfer Options Content */}
            {transferOpportunities.length > 0 ? renderTransferOpportunities() : (
              <StyledCard>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <TrendingUp sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    No Transfer Opportunities
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add credit card programs to see transfer opportunities to your airline programs
                  </Typography>
                </CardContent>
              </StyledCard>
            )}
          </Box>
        )}
      </Box>

      {/* Live Flight Map Section - Moved to Bottom */}
      {airlinePrograms.length > 0 && (
        <Box sx={{ mt: 6, mb: 4 }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            mb: 3, 
            textAlign: 'center',
            color: 'text.primary'
          }}>
            Explore Live Flight Data
          </Typography>
          
          <StyledCard sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              zIndex: 0
            }
          }}>
            <CardContent sx={{ position: 'relative', zIndex: 1, py: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Map />
                    Track Live Flights from Your Airlines
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
                    See real-time flights from {airlinePrograms.map(p => p.name).join(', ')} and discover earning opportunities
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.8 }}>
                    • Filter flights by your airline programs<br/>
                    • View flight paths and destinations<br/>
                    • Get personalized points earning tips<br/>
                    • Track domestic and international routes
                  </Typography>
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Map />}
                    onClick={() => window.location.href = '/flight-map'}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      fontSize: '1.1rem',
                      py: 1.5,
                      px: 3,
                      borderRadius: 3,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    Open Flight Map
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Box>
      )}

      <AddProgramDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddProgram}
        programType="airline"
      />

      {/* Flight Details Dialog */}
      <Dialog 
        open={flightDetailsOpen} 
        onClose={handleCloseFlightDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: (theme) => theme.palette.mode === 'light' 
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Flight />
            <Typography variant="h6">
              {selectedDeal?.route || 'Flight Details'}
            </Typography>
          </Box>
          <Button 
            onClick={handleCloseFlightDetails}
            sx={{ color: 'white', minWidth: 'auto', p: 1 }}
          >
            <Close />
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {selectedDeal && (
            <Box>
              {/* Route Information */}
              <Card sx={{ 
                mb: 3, 
                background: (theme) => theme.palette.mode === 'light' 
                  ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                  : 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
                color: (theme) => theme.palette.mode === 'light' ? 'inherit' : 'white'
              }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn color="primary" />
                    Route Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: '1 1 300px', minWidth: 200 }}>
                      <Typography variant="body2" color="text.secondary">Route</Typography>
                      <Typography variant="h6">{selectedDeal.route}</Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 300px', minWidth: 200 }}>
                      <Typography variant="body2" color="text.secondary">Class</Typography>
                      <Typography variant="h6">{selectedDeal.class}</Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 300px', minWidth: 200 }}>
                      <Typography variant="body2" color="text.secondary">Recommended Program</Typography>
                      <Typography variant="h6">{selectedDeal.airline}</Typography>
                    </Box>
                    {selectedDeal.operatingAirline && (
                      <Box sx={{ flex: '1 1 300px', minWidth: 200 }}>
                        <Typography variant="body2" color="text.secondary">Operating Airline</Typography>
                        <Typography variant="h6">{selectedDeal.operatingAirline}</Typography>
                      </Box>
                    )}
                    <Box sx={{ flex: '1 1 300px', minWidth: 200 }}>
                      <Typography variant="body2" color="text.secondary">Savings</Typography>
                      <Typography variant="h6" color="success.main">{selectedDeal.savings} vs cash</Typography>
                    </Box>
                    {selectedDeal.flightNumber && (
                      <Box sx={{ flex: '1 1 300px', minWidth: 200 }}>
                        <Typography variant="body2" color="text.secondary">Flight</Typography>
                        <Typography variant="h6">{selectedDeal.flightNumber}</Typography>
                      </Box>
                    )}
                    {selectedDeal.duration && (
                      <Box sx={{ flex: '1 1 300px', minWidth: 200 }}>
                        <Typography variant="body2" color="text.secondary">Duration</Typography>
                        <Typography variant="h6">{selectedDeal.duration}</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Pricing Details */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoney color="primary" />
                    Pricing Breakdown
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: '1 1 300px', minWidth: 200 }}>
                      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">Points Required</Typography>
                        <Typography variant="h5" color="primary">
                          {selectedDeal?.points?.toLocaleString() || 'N/A'} points
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flex: '1 1 300px', minWidth: 200 }}>
                      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">Cash Component</Typography>
                        <Typography variant="h5" color="secondary">
                          {selectedDeal?.formattedCash || `₹${selectedDeal?.cash?.toLocaleString() || 'N/A'}`}
                        </Typography>
                        {selectedDeal?.cashCurrency && selectedDeal?.cashCurrency !== 'INR' && (
                          <Typography variant="caption" color="text.secondary">
                            (Converted to {selectedDeal.cashCurrency})
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: (theme) => theme.palette.mode === 'light' ? 'success.light' : 'success.dark',
                    borderRadius: 1 
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: (theme) => theme.palette.mode === 'light' ? 'success.dark' : 'success.light'
                    }}>
                      Total Value
                    </Typography>
                    <Typography variant="h5" sx={{ 
                      color: (theme) => theme.palette.mode === 'light' ? 'success.dark' : 'success.light'
                    }}>
                      {selectedDeal?.points?.toLocaleString() || 'N/A'} points + {selectedDeal?.formattedCash || `₹${selectedDeal?.cash?.toLocaleString() || 'N/A'}`}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      mt: 1,
                      color: (theme) => theme.palette.mode === 'light' ? 'success.dark' : 'success.light'
                    }}>
                      {selectedDeal?.savings || 'N/A'} savings compared to cash booking
                      {selectedDeal?.isAmadeus && (
                        <Chip 
                          label="Real-time Data" 
                          size="small" 
                          color="success" 
                          sx={{ ml: 1 }} 
                        />
                      )}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Availability & Recommendations */}
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule color="primary" />
                    Recommendation Details
                  </Typography>
                  
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: (theme) => selectedDeal.canAfford 
                      ? (theme.palette.mode === 'light' ? 'success.light' : 'success.dark')
                      : (theme.palette.mode === 'light' ? 'warning.light' : 'warning.dark'),
                    borderRadius: 1,
                    mb: 2
                  }}>
                    <Typography variant="body1" sx={{ 
                      fontWeight: 600,
                      color: (theme) => selectedDeal.canAfford
                        ? (theme.palette.mode === 'light' ? 'success.dark' : 'success.light')
                        : (theme.palette.mode === 'light' ? 'warning.dark' : 'warning.light')
                    }}>
                      {selectedDeal.canAfford ? '✅ You can afford this booking!' : '⚠️ Additional points needed'}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      mt: 1,
                      color: (theme) => selectedDeal.canAfford
                        ? (theme.palette.mode === 'light' ? 'success.dark' : 'success.light')
                        : (theme.palette.mode === 'light' ? 'warning.dark' : 'warning.light')
                    }}>
                      {selectedDeal.recommendationReason}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Booking Tips:</strong>
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                      Book in advance for better availability
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                      Consider flexible dates for more options
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                      Check partner airline availability
                    </Typography>
                    <Typography component="li" variant="body2">
                      Monitor for award space openings
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseFlightDetails} color="inherit">
            Close
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            disabled={!selectedDeal?.canAfford}
            sx={{ ml: 1 }}
          >
            {selectedDeal?.canAfford ? 'Start Booking Process' : 'Earn More Points'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: (theme) => theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
          Delete Airline Program?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{programToDelete?.name}</strong>? 
            This action cannot be undone and will permanently remove the program and all associated data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              setProgramToDelete(null);
            }} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete}
            variant="contained" 
            color="error"
            sx={{ ml: 1 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};